import { Download, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { formatMeetingTimer } from './roomState';
import { fetchRecordingFile, listRecordings, recordingFileName } from '../api/recording';
import { RecordingApiError } from '../api/recording';
import { isActiveRecordingStatus } from '../api/recording';
import type { Recording, RecordingFile, RecordingFilePurpose } from '../api/recording';
import { cn } from '@/lib/utils';

/** Poll interval while any recording on the list is still not in a terminal state. */
const RECORDINGS_POLL_MS = 10000;

/** File-size label: bytes under 1 KB, one decimal for KB and MB. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} B`;
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(1)} KB`;
  }
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Longest known duration across the recording's files (the worker writes durationMs per
 * artifact; control runs no ffprobe itself). Null when nothing has been measured yet.
 */
export function recordingDuration(recording: Recording): number | null {
  let max: number | null = null;
  for (const file of recording.files) {
    if (file.durationMs !== null && (max === null || file.durationMs > max)) {
      max = file.durationMs;
    }
  }
  return max;
}

/** Localized wall-clock label of the recording start (date and short time). */
export function formatStartedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return iso;
  }
}

export interface RecordingsPanelProps {
  readonly room: string;
  /** Current join JWT for the Bearer calls; null while no token is held (fetches skip). */
  readonly getToken: () => string | null;
  /** Bumped by the page after a start/stop completes: forces an out-of-band refresh. */
  readonly refreshKey: number;
  /** Refresh interval override for tests; production keeps 10 s. */
  readonly pollMs?: number | undefined;
  /** Reports a localized failure line to the page's toast area. */
  readonly onError: (message: string) => void;
}

/**
 * The «Записи» tab (moderators/admins only - the tab itself is gated by the SidePanel): the
 * room's recording history with per-purpose file rows and Bearer downloads. The list refreshes
 * on tab open, after every start/stop (refreshKey) and polls while any recording is still not
 * in a terminal state (the worker pipeline keeps flipping statuses after the recording ends).
 */
export function RecordingsPanel(props: RecordingsPanelProps): JSX.Element {
  const { t } = useTranslation();
  const [recordings, setRecordings] = useState<readonly Recording[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  // A refresh that lost the race against a newer one must not overwrite the fresher list.
  const refreshSeqRef = useRef(0);
  // Downloads started before a refresh still resolve against the recording they came from.
  const tokenRef = useRef<string | null>(null);
  tokenRef.current = props.getToken();

  const refresh = useCallback(async (): Promise<void> => {
    const token = tokenRef.current;
    if (token === null) {
      return;
    }
    const seq = refreshSeqRef.current + 1;
    refreshSeqRef.current = seq;
    try {
      const list = await listRecordings(props.room, token);
      if (refreshSeqRef.current === seq) {
        setRecordings(list);
        setLoadFailed(false);
      }
    } catch {
      if (refreshSeqRef.current === seq) {
        setLoadFailed(true);
      }
    }
  }, [props.room]);

  useEffect(() => {
    void refresh();
  }, [refresh, props.refreshKey]);

  const hasActive =
    recordings?.some((recording) => isActiveRecordingStatus(recording.status)) ?? false;
  const pollMs = props.pollMs ?? RECORDINGS_POLL_MS;
  useEffect(() => {
    if (!hasActive) {
      return;
    }
    const timer = window.setInterval(() => {
      void refresh();
    }, pollMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [hasActive, pollMs, refresh]);

  async function download(recording: Recording, file: RecordingFile): Promise<void> {
    const token = tokenRef.current;
    if (token === null || downloadingId !== null) {
      return;
    }
    setDownloadingId(file.id);
    try {
      const blob = await fetchRecordingFile(props.room, recording.id, file.id, token);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = recordingFileName(props.room, recording.id, file.purpose);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (err: unknown) {
      props.onError(
        err instanceof RecordingApiError && err.code === 'not_found'
          ? t('recording.download_not_ready')
          : t('recording.download_failed'),
      );
    } finally {
      setDownloadingId(null);
    }
  }

  const purposes = groupByPurpose(recordings);

  return (
    <div data-testid="recordings-panel" className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between px-3 py-2">
        <p className="text-[11px] uppercase tracking-wide text-zinc-500">
          {t('recording.panel_title')}
        </p>
        <button
          type="button"
          data-testid="recordings-refresh"
          aria-label={t('recording.refresh_aria')}
          title={t('recording.refresh_aria')}
          onClick={() => {
            void refresh();
          }}
          className="grid h-7 w-7 place-items-center rounded-md text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
        aria-label={t('recording.panel_aria')}
      >
        {loadFailed ? (
          <p role="alert" data-testid="recordings-error" className="px-2 py-4 text-xs text-red-300">
            {t('recording.load_failed')}
          </p>
        ) : null}
        {recordings !== null && recordings.length === 0 && !loadFailed ? (
          <p className="px-2 py-4 text-center text-xs text-zinc-500">{t('recording.empty')}</p>
        ) : null}
        {purposes !== null
          ? purposes.map((recording) => (
              <RecordingRow
                key={recording.recording.id}
                recording={recording.recording}
                groups={recording.groups}
                downloadingId={downloadingId}
                onDownload={(file) => {
                  void download(recording.recording, file);
                }}
              />
            ))
          : null}
      </div>
    </div>
  );
}

interface PurposeGroup {
  readonly purpose: RecordingFilePurpose;
  readonly files: readonly RecordingFile[];
}

/** Files grouped by purpose in the fixed purpose order (audio, video, mp3, transcript, merged). */
function groupByPurpose(
  recordings: readonly Recording[] | null,
): readonly { readonly recording: Recording; readonly groups: readonly PurposeGroup[] }[] | null {
  if (recordings === null) {
    return null;
  }
  const order: readonly RecordingFilePurpose[] = ['audio', 'video', 'mp3', 'transcript', 'merged', 'summary'];
  return recordings.map((recording) => {
    const groups: PurposeGroup[] = [];
    for (const purpose of order) {
      const files = recording.files.filter((file) => file.purpose === purpose);
      if (files.length > 0) {
        groups.push({ purpose, files });
      }
    }
    return { recording, groups };
  });
}

interface RecordingRowProps {
  readonly recording: Recording;
  readonly groups: readonly PurposeGroup[];
  readonly downloadingId: number | null;
  readonly onDownload: (file: RecordingFile) => void;
}

/** One recording: id, start time, duration, status badge, then the per-purpose file groups. */
function RecordingRow(props: RecordingRowProps): JSX.Element {
  const { t } = useTranslation();
  const recording = props.recording;
  const durationMs = recordingDuration(recording);
  const statusTone =
    recording.status === 'failed'
      ? 'border-red-500/40 bg-red-500/10 text-red-300'
      : isActiveRecordingStatus(recording.status)
        ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
        : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300';
  return (
    <section
      data-testid={`recording-row-${String(recording.id)}`}
      className="mb-3 rounded-lg border border-white/10 bg-zinc-900/60 p-2.5"
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs font-semibold text-zinc-100">
          #{String(recording.id)}
        </span>
        <span
          data-testid={`rec-status-${String(recording.id)}`}
          className={cn('rounded border px-1.5 py-0.5 text-[10px] font-medium', statusTone)}
        >
          {t(`recording.state_${recording.status}`)}
        </span>
      </div>
      <div className="mt-1 grid grid-cols-2 gap-x-2 text-[11px] text-zinc-400">
        <span>
          <span className="text-zinc-500">{t('recording.column_started')}: </span>
          {formatStartedAt(recording.startedAt)}
        </span>
        <span>
          <span className="text-zinc-500">{t('recording.column_duration')}: </span>
          {durationMs === null ? '—' : formatMeetingTimer(Math.round(durationMs / 1000))}
        </span>
      </div>
      <ul className="mt-2 flex flex-col gap-1" aria-label={t('recording.files_aria')}>
        {props.groups.map((group) => (
          <li key={group.purpose} className="flex flex-col gap-1">
            <p className="text-[10px] uppercase tracking-wide text-zinc-500">
              {t(`recording.purpose_${group.purpose}`)}
            </p>
            {group.files.map((file) => (
              <FileRow
                key={file.id}
                file={file}
                recordingId={recording.id}
                downloading={props.downloadingId === file.id}
                onDownload={() => {
                  props.onDownload(file);
                }}
              />
            ))}
          </li>
        ))}
      </ul>
    </section>
  );
}

interface FileRowProps {
  readonly file: RecordingFile;
  readonly recordingId: number;
  readonly downloading: boolean;
  readonly onDownload: () => void;
}

/** One file of a recording: purpose-scoped download button, or its pending/missing status. */
function FileRow(props: FileRowProps): JSX.Element {
  const { t } = useTranslation();
  const file = props.file;
  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-zinc-800/50 px-2 py-1">
      <span className="min-w-0 truncate text-[11px] text-zinc-300">
        {file.sizeBytes !== null
          ? formatFileSize(file.sizeBytes)
          : t(`recording.file_status_${file.status}`)}
      </span>
      {file.status === 'ready' ? (
        <button
          type="button"
          data-testid={`rec-download-${file.purpose}-${String(file.id)}`}
          aria-label={t('recording.download_aria', {
            purpose: t(`recording.purpose_${file.purpose}`),
          })}
          disabled={props.downloading}
          onClick={props.onDownload}
          className="flex h-6 shrink-0 items-center gap-1 rounded-md border border-white/10 px-1.5 text-[10px] font-medium text-zinc-300 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Download className="h-3 w-3" />
          {props.downloading ? t('recording.downloading') : t('recording.download')}
        </button>
      ) : (
        <span
          data-testid={`rec-file-status-${String(file.id)}`}
          className="shrink-0 text-[10px] text-zinc-500"
        >
          {t(`recording.file_status_${file.status}`)}
        </span>
      )}
    </div>
  );
}
